import {
  ConversationMessage as ConversationMessageEntity,
  ConversationMessageThread as ConversationMessageThreadEntity,
} from '@provision/entity-types';
import { useApolloClient } from '@provision/graphql-client';
import { useEffect } from 'react';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { wsEventBus } from 'src/core/realtime-connection';
import { writeThreadInCache } from 'src/graphql/entities/conversation-message-reply/conversation-message-reply.cache';
import { GET_MESSAGE_THREAD_QUERY } from 'src/graphql/entities/conversation-message-reply/conversation-message-reply.operations';

/**
 * Applies replies and thread resolutions made by anyone to the cached messages of one
 * conversation. A reply event carries no author, so the message's thread is refetched; a thread
 * event is complete and is written as is.
 */
export const useMessageThreadSubscription = (conversationId?: string) => {
  const client = useApolloClient();

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    const replySubscription = merge(
      wsEventBus.onEntity<ConversationMessageEntity>({
        name: 'ConversationMessage',
        operation: 'create',
      }),
      wsEventBus.onEntity<ConversationMessageEntity>({
        name: 'ConversationMessage',
        operation: 'update',
      }),
    )
      .pipe(
        filter(
          ({ attributes }) =>
            attributes.conversationId === conversationId &&
            Boolean(attributes.parentConversationMessageId),
        ),
      )
      .subscribe(({ attributes }) => {
        client
          .query({
            query: GET_MESSAGE_THREAD_QUERY,
            variables: {
              params: {
                filters: [
                  {
                    attribute: 'conversationMessageId',
                    operator: '=',
                    value: attributes.parentConversationMessageId,
                  },
                ],
                limit: 1,
              },
            },
            fetchPolicy: 'network-only',
            context: { ttl: 0 },
          })
          .catch((error: unknown) => console.error(error));
      });

    const threadSubscription = merge(
      wsEventBus.onEntity<ConversationMessageThreadEntity>({
        name: 'ConversationMessageThread',
        operation: 'create',
      }),
      wsEventBus.onEntity<ConversationMessageThreadEntity>({
        name: 'ConversationMessageThread',
        operation: 'update',
      }),
    )
      .pipe(filter(({ attributes }) => attributes.conversationId === conversationId))
      .subscribe(({ attributes }) =>
        writeThreadInCache(client.cache, attributes.conversationMessageId, {
          id: attributes.id,
          conversationMessageThreadId: attributes.conversationMessageThreadId,
          conversationMessageId: attributes.conversationMessageId,
          conversationId: attributes.conversationId,
          partnerId: attributes.partnerId,
          resolutionConversationMessageId: attributes.resolutionConversationMessageId ?? null,
          resolvedByUserId: attributes.resolvedByUserId ?? null,
          resolvedAt: attributes.resolvedAt ?? null,
        }),
      );

    return () => {
      replySubscription.unsubscribe();
      threadSubscription.unsubscribe();
    };
  }, [client, conversationId]);
};
